import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDevAuth } from '../utils/useDevAuth';
import { useAxiosClient } from '../utils/useAxiosClient';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MessageIcon from '@mui/icons-material/Message';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import WarningIcon from '@mui/icons-material/Warning';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditIcon from '@mui/icons-material/Edit';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import LinearProgress from '@mui/material/LinearProgress';

interface Conversation {
  id: string;
  org_id: number;
  user_id: number;
  title: string | null;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
  organization_name: string;
  user_email: string;
  message_count: number;
}

interface ToolCommand {
  tool: string;
  args: Record<string, unknown>;
  success?: boolean;
  error?: string;
  traceback?: string;
}

interface ToolMetadata {
  commands?: ToolCommand[];
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
  is_complete: boolean;
  tool_metadata?: ToolMetadata;
}

interface ConversationDetail extends Conversation {
  messages: Message[];
}

interface Stats {
  total_conversations: number;
  total_messages: number;
  conversations_last_7_days: number;
  incomplete_messages: number;
  conversations_by_org: { org_name: string; count: number }[];
  most_active_users: { email: string; count: number }[];
}

interface IncompleteMessage {
  id: string;
  conversation_id: string;
  role: string;
  content: string;
  created_at: string;
  conversation_title: string | null;
  organization_name: string;
  user_email: string;
  age_minutes: number;
  is_stale: boolean;
}

interface UserUsage {
  user_id: number;
  email: string;
  requests: number;
  cost_usd: number;
  prompt_tokens: number;
  completion_tokens: number;
  weekly_limit_usd: number;
  period_cost_usd: number;
  is_unlimited: boolean;
  is_over_limit: boolean;
}

interface UsageData {
  period_start: string;
  period_end: string;
  users: UserUsage[];
}

/**
 * Admin Chatbot Logs - View all IL-2 chatbot conversations
 */
const AdminChatbotLogs: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useDevAuth();
  const axiosClient = useAxiosClient();

  // State
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Filters
  const [orgFilter, setOrgFilter] = useState<string>('');
  const [searchEmail, setSearchEmail] = useState('');
  
  // Dialog state
  const [selectedConversation, setSelectedConversation] = useState<ConversationDetail | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  
  // Incomplete messages state
  const [incompleteMessages, setIncompleteMessages] = useState<IncompleteMessage[]>([]);
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  
  // Usage/limits state
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [showUsage, setShowUsage] = useState(true);
  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [editLimit, setEditLimit] = useState<string>('');
  const [editUnlimited, setEditUnlimited] = useState(false);

  // Check if user is admin
  const provider = user?.sub?.split('|')[0];
  const emailLower = user?.email?.toLowerCase() || '';
  const isAdmin = (provider === 'google-oauth2' || provider === 'clerk') && (emailLower === 'weezilla@gmail.com' || emailLower.endsWith('@crant.ai'));

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await axiosClient.get('/api/admin/conversations/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [axiosClient]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', rowsPerPage.toString());
      params.append('offset', (page * rowsPerPage).toString());
      if (orgFilter) params.append('org_id', orgFilter);
      
      const response = await axiosClient.get(`/api/admin/conversations?${params.toString()}`);
      setConversations(response.data.conversations);
      setTotalCount(response.data.total_count);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [axiosClient, page, rowsPerPage, orgFilter]);

  // Fetch conversation detail
  const fetchConversationDetail = async (conversationId: string) => {
    setDialogLoading(true);
    setDialogOpen(true);
    try {
      const response = await axiosClient.get(`/api/admin/conversations/${conversationId}`);
      setSelectedConversation(response.data);
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
    } finally {
      setDialogLoading(false);
    }
  };

  // Fetch incomplete messages
  const fetchIncompleteMessages = useCallback(async () => {
    try {
      const response = await axiosClient.get('/api/admin/conversations/incomplete');
      setIncompleteMessages(response.data.incomplete_messages || []);
    } catch (error) {
      console.error('Failed to fetch incomplete messages:', error);
    }
  }, [axiosClient]);

  // Fetch usage data
  const fetchUsageData = useCallback(async () => {
    setUsageLoading(true);
    try {
      const response = await axiosClient.get('/api/admin/usage/all-users');
      setUsageData(response.data);
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
    } finally {
      setUsageLoading(false);
    }
  }, [axiosClient]);

  // Update user limit
  const handleUpdateLimit = async (userId: number) => {
    try {
      const payload: { weekly_limit_usd?: number; is_unlimited?: boolean } = {};
      
      if (editUnlimited) {
        payload.is_unlimited = true;
      } else {
        payload.is_unlimited = false;
        payload.weekly_limit_usd = parseFloat(editLimit) || 40;
      }
      
      await axiosClient.put(`/api/admin/users/${userId}/quota`, payload);
      setEditingUser(null);
      fetchUsageData();
    } catch (error) {
      console.error('Failed to update limit:', error);
      alert('Failed to update user limit');
    }
  };

  // Start editing a user's limit
  const startEditLimit = (user: UserUsage) => {
    setEditingUser(user.user_id);
    setEditLimit(user.weekly_limit_usd.toString());
    setEditUnlimited(user.is_unlimited);
  };

  // Cleanup stale messages
  const handleCleanup = async () => {
    setCleaningUp(true);
    try {
      const response = await axiosClient.post('/api/admin/conversations/cleanup');
      alert(`Cleaned up ${response.data.cleaned_count} stale messages`);
      fetchStats();
      fetchIncompleteMessages();
    } catch (error) {
      console.error('Failed to cleanup:', error);
      alert('Failed to cleanup stale messages');
    } finally {
      setCleaningUp(false);
    }
  };

  // Delete single message
  const handleDeleteMessage = async (messageId: string) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await axiosClient.delete(`/api/admin/conversations/message/${messageId}`);
      fetchStats();
      fetchIncompleteMessages();
    } catch (error) {
      console.error('Failed to delete message:', error);
      alert('Failed to delete message');
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      fetchConversations();
      fetchIncompleteMessages();
      fetchUsageData();
    }
  }, [isAdmin, fetchStats, fetchConversations, fetchIncompleteMessages, fetchUsageData]);

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Format relative time
  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">Unauthorized</Typography>
        <Typography>You do not have access to this page.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      <Button 
        variant="text" 
        onClick={() => navigate('/admin')}
        sx={{ mb: 2 }}
      >
        ← Back to Admin Dashboard
      </Button>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Chatbot Usage Logs
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={() => { fetchStats(); fetchConversations(); fetchIncompleteMessages(); fetchUsageData(); }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ChatIcon sx={{ fontSize: 40, color: '#7b1fa2', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {statsLoading ? <CircularProgress size={24} /> : stats?.total_conversations || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Conversations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <MessageIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {statsLoading ? <CircularProgress size={24} /> : stats?.total_messages || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Messages
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <BusinessIcon sx={{ fontSize: 40, color: '#388e3c', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {statsLoading ? <CircularProgress size={24} /> : stats?.conversations_by_org?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Organizations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 40, color: '#f57c00', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {statsLoading ? <CircularProgress size={24} /> : stats?.conversations_last_7_days || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last 7 Days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Users & Orgs */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Top Organizations
              </Typography>
              {stats.conversations_by_org.slice(0, 5).map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2">{item.org_name}</Typography>
                  <Chip label={item.count} size="small" color="primary" />
                </Box>
              ))}
              {stats.conversations_by_org.length === 0 && (
                <Typography variant="body2" color="text.secondary">No data yet</Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Most Active Users
              </Typography>
              {stats.most_active_users.slice(0, 5).map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.email}
                  </Typography>
                  <Chip label={item.count} size="small" color="secondary" />
                </Box>
              ))}
              {stats.most_active_users.length === 0 && (
                <Typography variant="body2" color="text.secondary">No data yet</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Usage & Limits Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setShowUsage(!showUsage)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoneyIcon sx={{ color: '#2e7d32' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Usage & Limits
            </Typography>
            {usageData && (
              <Chip 
                label={`$${usageData.users.reduce((sum, u) => sum + u.cost_usd, 0).toFixed(2)} this week`}
                size="small"
                color="success"
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {showUsage ? '▼' : '▶'}
          </Typography>
        </Box>
        
        {showUsage && (
          <Box sx={{ mt: 2 }}>
            {usageData && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Period: {new Date(usageData.period_start).toLocaleDateString()} - {new Date(usageData.period_end).toLocaleDateString()}
              </Typography>
            )}
            
            {usageLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : usageData?.users.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No usage data for this period
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Requests</TableCell>
                      <TableCell align="right">Cost</TableCell>
                      <TableCell>Usage</TableCell>
                      <TableCell align="right">Limit</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageData?.users.map((user) => (
                      <TableRow key={user.user_id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{user.requests}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              color: user.is_over_limit ? '#d32f2f' : 'inherit'
                            }}
                          >
                            ${user.cost_usd.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          {user.is_unlimited ? (
                            <Chip 
                              icon={<AllInclusiveIcon />}
                              label="Unlimited" 
                              size="small" 
                              color="info"
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={Math.min((user.period_cost_usd / user.weekly_limit_usd) * 100, 100)}
                                sx={{ 
                                  flex: 1, 
                                  height: 8, 
                                  borderRadius: 4,
                                  bgcolor: 'rgba(0,0,0,0.1)',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: user.is_over_limit ? '#d32f2f' : 
                                             (user.period_cost_usd / user.weekly_limit_usd) > 0.8 ? '#ff9800' : '#4caf50'
                                  }
                                }}
                              />
                              <Typography variant="caption" sx={{ minWidth: 35 }}>
                                {((user.period_cost_usd / user.weekly_limit_usd) * 100).toFixed(0)}%
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {editingUser === user.user_id ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FormControl size="small" sx={{ minWidth: 80 }}>
                                <Select
                                  value={editUnlimited ? 'unlimited' : 'limited'}
                                  onChange={(e) => setEditUnlimited(e.target.value === 'unlimited')}
                                  size="small"
                                >
                                  <MenuItem value="limited">Limited</MenuItem>
                                  <MenuItem value="unlimited">Unlimited</MenuItem>
                                </Select>
                              </FormControl>
                              {!editUnlimited && (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editLimit}
                                  onChange={(e) => setEditLimit(e.target.value)}
                                  sx={{ width: 80 }}
                                  InputProps={{ startAdornment: '$' }}
                                />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2">
                              {user.is_unlimited ? '∞' : `$${user.weekly_limit_usd.toFixed(0)}/wk`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {editingUser === user.user_id ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button 
                                size="small" 
                                variant="contained"
                                onClick={() => handleUpdateLimit(user.user_id)}
                              >
                                Save
                              </Button>
                              <Button 
                                size="small"
                                onClick={() => setEditingUser(null)}
                              >
                                Cancel
                              </Button>
                            </Box>
                          ) : (
                            <IconButton 
                              size="small"
                              onClick={() => startEditLimit(user)}
                              title="Edit limit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Incomplete Messages Alert */}
      {(stats?.incomplete_messages || 0) > 0 && (
        <Paper 
          sx={{ 
            p: 2, 
            mb: 3, 
            bgcolor: '#fff3e0', 
            border: '1px solid #ffb74d',
            cursor: 'pointer',
          }}
          onClick={() => setShowIncomplete(!showIncomplete)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon sx={{ color: '#f57c00' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {stats?.incomplete_messages} Incomplete Message{stats?.incomplete_messages !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                (click to {showIncomplete ? 'hide' : 'view'})
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={cleaningUp ? <CircularProgress size={16} /> : <CleaningServicesIcon />}
              onClick={(e) => { e.stopPropagation(); handleCleanup(); }}
              disabled={cleaningUp}
              sx={{ bgcolor: '#f57c00', '&:hover': { bgcolor: '#ef6c00' } }}
            >
              Cleanup Stale
            </Button>
          </Box>
          
          {showIncomplete && incompleteMessages.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Age</TableCell>
                      <TableCell>Organization</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Content Preview</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {incompleteMessages.map((msg) => (
                      <TableRow key={msg.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {msg.age_minutes.toFixed(1)}m
                          </Typography>
                        </TableCell>
                        <TableCell>{msg.organization_name}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {msg.user_email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              maxWidth: 200, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '0.75rem',
                            }}
                          >
                            {msg.content || <em>Empty</em>}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={msg.is_stale ? 'Stale' : 'In Progress'} 
                            size="small" 
                            color={msg.is_stale ? 'error' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteMessage(msg.id)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Organization</InputLabel>
              <Select
                value={orgFilter}
                label="Filter by Organization"
                onChange={(e) => { setOrgFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Organizations</MenuItem>
                {stats?.conversations_by_org.map((item) => (
                  <MenuItem key={item.org_name} value={item.org_name}>
                    {item.org_name} ({item.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Search by email"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              placeholder="Filter displayed results..."
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Conversations Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>User</TableCell>
                <TableCell align="center">Messages</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : conversations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No conversations found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                conversations
                  .filter(conv => 
                    !searchEmail || 
                    conv.user_email.toLowerCase().includes(searchEmail.toLowerCase())
                  )
                  .map((conv) => (
                    <TableRow key={conv.id} hover>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: 250, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {conv.title || <em style={{ color: '#999' }}>Untitled</em>}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={conv.organization_name} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {conv.user_email}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={conv.message_count} 
                          size="small" 
                          color={conv.message_count > 10 ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {formatRelativeTime(conv.updated_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {formatDate(conv.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          onClick={() => fetchConversationDetail(conv.id)}
                          title="View conversation"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Conversation Detail Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {selectedConversation?.title || 'Untitled Conversation'}
            </Typography>
            {selectedConversation && (
              <Typography variant="caption" color="text.secondary">
                {selectedConversation.user_email} • {selectedConversation.organization_name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedConversation?.messages ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedConversation.messages.map((msg) => (
                <Paper
                  key={msg.id}
                  elevation={1}
                  sx={{
                    p: 2,
                    bgcolor: msg.role === 'user' ? '#e3f2fd' : '#f5f5f5',
                    borderLeft: msg.role === 'user' 
                      ? '4px solid #1976d2' 
                      : '4px solid #7b1fa2',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                      {msg.role}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(msg.created_at)}
                      {!msg.is_complete && (
                        <Chip label="Incomplete" size="small" color="warning" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: msg.role === 'assistant' ? 'inherit' : 'inherit',
                    }}
                  >
                    {msg.content}
                  </Typography>
                  
                  {/* Show tool executions for assistant messages */}
                  {msg.role === 'assistant' && msg.tool_metadata?.commands && msg.tool_metadata.commands.length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #ccc' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#7b1fa2', mb: 1, display: 'block' }}>
                        🛠️ Tool Executions ({msg.tool_metadata.commands.length})
                      </Typography>
                      {msg.tool_metadata.commands.map((cmd, idx) => (
                        <Box 
                          key={idx} 
                          sx={{ 
                            mt: 1, 
                            p: 1.5, 
                            bgcolor: cmd.success === false ? '#2d1b1b' : '#1e1e1e', 
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            color: '#d4d4d4',
                            overflow: 'auto',
                            maxHeight: 400,
                            border: cmd.success === false ? '1px solid #d32f2f' : 'none',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: cmd.success === false ? '#f44336' : '#569cd6', 
                                fontWeight: 600, 
                                fontFamily: 'monospace',
                              }}
                            >
                              {cmd.success === false ? '❌' : '✅'} {cmd.tool}
                            </Typography>
                            {cmd.success === false && (
                              <Chip 
                                label="FAILED" 
                                size="small" 
                                sx={{ 
                                  height: 16, 
                                  fontSize: '0.65rem',
                                  bgcolor: '#d32f2f',
                                  color: 'white',
                                }}
                              />
                            )}
                          </Box>
                          {/* Show error message if failed */}
                          {cmd.success === false && cmd.error && (
                            <Box sx={{ 
                              bgcolor: '#3d1515', 
                              p: 1, 
                              borderRadius: 0.5, 
                              mb: 1,
                              border: '1px solid #d32f2f',
                            }}>
                              <Typography variant="caption" sx={{ color: '#ff6b6b', fontFamily: 'monospace', display: 'block' }}>
                                Error: {cmd.error}
                              </Typography>
                              {cmd.traceback && (
                                <details style={{ marginTop: 4 }}>
                                  <summary style={{ cursor: 'pointer', color: '#ff9999', fontSize: '0.7rem' }}>
                                    Show Traceback
                                  </summary>
                                  <pre style={{ 
                                    margin: '4px 0 0 0', 
                                    whiteSpace: 'pre-wrap', 
                                    wordBreak: 'break-word',
                                    color: '#ffaaaa',
                                    fontSize: '0.65rem',
                                  }}>
                                    {cmd.traceback}
                                  </pre>
                                </details>
                              )}
                            </Box>
                          )}
                          {cmd.tool === 'run_pandas_code_tool' && cmd.args?.code ? (
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {String(cmd.args.code)}
                            </pre>
                          ) : cmd.tool === 'generate_chartjs_tool' ? (
                            <Typography variant="caption" sx={{ color: '#ce9178', fontFamily: 'monospace' }}>
                              Chart: {String(cmd.args?.title || 'Untitled')} ({String(cmd.args?.chartType || 'unknown')})
                            </Typography>
                          ) : (
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {JSON.stringify(cmd.args, null, 2)}
                            </pre>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              ))}
              {selectedConversation.messages.length === 0 && (
                <Typography color="text.secondary" align="center">
                  No messages in this conversation
                </Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminChatbotLogs;
